import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MdMap, MdStar, MdAccessTime, MdFavorite } from 'react-icons/md';
import api from '../utils/api';
import Header from '../components/Header';
import InfiniteCarousel from '../components/InfiniteCarousel';
import { getPasseiosLang } from '../utils/passeioLang';

function Home() {
  const { t, i18n } = useTranslation();
  const [passeios, setPasseios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [whatsappGlobal, setWhatsappGlobal] = useState('');

  useEffect(() => {
    fetchPasseios();
    fetchWhatsappGlobal();
  }, []);

  const fetchWhatsappGlobal = async () => {
    try {
      const response = await api.get('/configuracoes/whatsapp');
      setWhatsappGlobal(response.data.whatsapp || '');
    } catch (err) {
      console.error('Erro ao buscar WhatsApp global:', err);
    }
  };

  const handleWhatsAppClick = () => {
    if (!whatsappGlobal) {
      return;
    }

    const mensagem = t('home.purchaseBanner.whatsappMessage');
    const whatsappUrl = `https://wa.me/${whatsappGlobal}?text=${encodeURIComponent(mensagem)}`;
    window.open(whatsappUrl, '_blank');
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const elements = document.querySelectorAll('.animate-on-scroll');
      elements.forEach(el => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          el.classList.remove('opacity-0');
        }
      });

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('opacity-0');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });

      elements.forEach(el => observer.observe(el));
      window.scrollObserver = observer;
    }, 100);

    return () => {
      clearTimeout(timer);
      if (window.scrollObserver) {
        window.scrollObserver.disconnect();
      }
    };
  }, [passeios, loading]);

  const fetchPasseios = async () => {
    try {
      const response = await api.get('/passeios');
      setPasseios(response.data);
      setLoading(false);
    } catch (err) {
      setError('Erro ao carregar passeios');
      setLoading(false);
    }
  };

  // Obter passeios no idioma correto
  const passeiosLang = getPasseiosLang(passeios, i18n.language);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-lg text-gray-600 animate-pulse">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 overflow-x-hidden">
      <Header />
      <div className="pt-20">

      {/* Hero Banner */}
      <section 
        id="inicio" 
        className="relative bg-gradient-to-b from-stone-900 via-stone-800 to-stone-900 text-white min-h-[90vh] flex items-center overflow-hidden"
      >
        {/* Banner de fundo */}
        <div className="absolute inset-0">
          <img 
            src="/banner-cataratas.jpg" 
            alt="Cataratas do Iguaçu - Panorama aéreo" 
            className="w-full h-full object-cover object-center"
            onError={(e) => {
              // Fallback caso a imagem não esteja disponível
              e.target.src = 'https://images.unsplash.com/photo-1547036967-23d11aacaee0?w=1920&h=1080&fit=crop&q=80';
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-900/50 via-stone-900/40 to-stone-900/50"></div>
        </div>

        {/* Imagem do tucano voando no canto superior esquerdo com animação */}
        <div className="absolute left-0 top-0 z-20 pointer-events-none translate-x-4 sm:translate-x-6 md:translate-x-8 lg:translate-x-12 translate-y-24 sm:translate-y-32 md:translate-y-40 lg:translate-y-48">
          <img 
            src="/tucano_voando.png" 
            alt="Tucano voando" 
            className="w-32 sm:w-40 md:w-48 lg:w-64 h-auto animate-fly-random opacity-90"
            style={{ filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.3))' }}
          />
        </div>

        {/* Imagem do tucano no canto direito com animação */}
        <div className="absolute right-0 bottom-0 z-20 pointer-events-none translate-y-8 sm:translate-y-12 md:translate-y-16 translate-x-8 sm:translate-x-12 md:translate-x-16 lg:translate-x-20">
          <img 
            src="/tucano.png" 
            alt="Tucano" 
            className="w-48 sm:w-56 md:w-72 lg:w-96 h-auto animate-rotate-gentle opacity-90"
            style={{ filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.3))', transformOrigin: 'center bottom' }}
          />
        </div>

        <div className="container mx-auto px-4 relative z-10 py-32">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-light mb-6 tracking-wide text-white">
              {t('home.title')}
            </h1>
            <p className="text-xl md:text-2xl text-stone-200 mb-10 font-light">
              {t('home.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => {
                  const element = document.getElementById('passeios');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-gradient-to-r from-orange-500 to-yellow-400 text-white px-8 py-4 rounded-lg font-semibold hover:from-orange-600 hover:to-yellow-500 transition-all duration-300 shadow-lg"
              >
                {t('home.viewTours')}
              </button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <svg className="w-6 h-6 text-white/70" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
            <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
          </svg>
        </div>
      </section>

      {/* Stats Section - Refined Compact Style */}
      <section className="py-8 bg-white border-y border-stone-200">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {/* Passeios */}
              <div className="flex items-center gap-3 p-4 md:p-5 bg-stone-50 rounded-lg border border-stone-200/60 hover:border-stone-300 hover:bg-stone-100/50 transition-all duration-200 animate-on-scroll opacity-0 group">
                <div className="flex-shrink-0 p-2 bg-white rounded-lg border border-stone-100 group-hover:shadow-sm transition-shadow">
                  <MdMap className="w-5 h-5 md:w-6 md:h-6 text-stone-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xl md:text-2xl font-bold text-stone-900 leading-tight mb-0.5">
                    10+
                  </div>
                  <div className="text-xs text-stone-600 font-medium">{t('home.stats.tours')}</div>
                </div>
              </div>

              {/* Qualificados */}
              <div className="flex items-center gap-3 p-4 md:p-5 bg-stone-50 rounded-lg border border-stone-200/60 hover:border-stone-300 hover:bg-stone-100/50 transition-all duration-200 animate-on-scroll opacity-0 group" style={{ animationDelay: '0.1s' }}>
                <div className="flex-shrink-0 p-2 bg-white rounded-lg border border-stone-100 group-hover:shadow-sm transition-shadow">
                  <MdStar className="w-5 h-5 md:w-6 md:h-6 text-stone-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xl md:text-2xl font-bold text-stone-900 leading-tight mb-0.5">
                    100%
                  </div>
                  <div className="text-xs text-stone-600 font-medium">{t('home.stats.qualified')}</div>
                </div>
              </div>

              {/* Atendimento */}
              <div className="flex items-center gap-3 p-4 md:p-5 bg-stone-50 rounded-lg border border-stone-200/60 hover:border-stone-300 hover:bg-stone-100/50 transition-all duration-200 animate-on-scroll opacity-0 group" style={{ animationDelay: '0.2s' }}>
                <div className="flex-shrink-0 p-2 bg-white rounded-lg border border-stone-100 group-hover:shadow-sm transition-shadow">
                  <MdAccessTime className="w-5 h-5 md:w-6 md:h-6 text-stone-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xl md:text-2xl font-bold text-stone-900 leading-tight mb-0.5">
                    24/7
                  </div>
                  <div className="text-xs text-stone-600 font-medium">{t('home.stats.support')}</div>
                </div>
              </div>

              {/* Experiências */}
              <div className="flex items-center gap-3 p-4 md:p-5 bg-stone-50 rounded-lg border border-stone-200/60 hover:border-stone-300 hover:bg-stone-100/50 transition-all duration-200 animate-on-scroll opacity-0 group" style={{ animationDelay: '0.3s' }}>
                <div className="flex-shrink-0 p-2 bg-white rounded-lg border border-stone-100 group-hover:shadow-sm transition-shadow">
                  <MdFavorite className="w-5 h-5 md:w-6 md:h-6 text-stone-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xl md:text-2xl font-bold text-stone-900 leading-tight mb-0.5">
                    ∞
                  </div>
                  <div className="text-xs text-stone-600 font-medium">{t('home.stats.experiences')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Seção Informativa - Transfer e Ingressos */}
      <section className="py-16 relative">
        <div className="absolute inset-0">
          <img 
            src="/ingressos.jpg" 
            alt="Ingressos" 
            className="w-full h-full object-cover object-right"
            style={{ objectPosition: 'right center' }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <div className="absolute inset-0 bg-stone-900/30"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-md border border-stone-200 overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6 p-8">
                {/* Conteúdo Textual */}
                <div className="md:col-span-3 flex flex-col justify-center">
                  <h3 className="text-2xl md:text-3xl font-light text-stone-800 mb-4">
                    {t('home.purchaseBanner.title')}
                  </h3>
                  <p className="text-stone-600 text-lg mb-3">
                    {t('home.purchaseBanner.subtitle')}
                  </p>
                  <p className="text-stone-500 text-base leading-relaxed">
                    {t('home.purchaseBanner.description')}
                  </p>
                </div>
                
                {/* Ícone e Botão */}
                <div className="md:col-span-2 flex flex-col items-center justify-center gap-6 border-t md:border-t-0 md:border-l border-stone-200 pt-6 md:pt-0 md:pl-6">
                  <div className="flex items-center gap-3">
                    <div className="bg-stone-100 rounded-full p-4">
                      <svg className="w-10 h-10 text-stone-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                      </svg>
                    </div>
                    <div className="bg-stone-100 rounded-full p-4">
                      <svg className="w-10 h-10 text-stone-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                      </svg>
                    </div>
                  </div>
                  {whatsappGlobal && (
                    <button
                      onClick={handleWhatsAppClick}
                      className="w-full bg-stone-800 hover:bg-stone-700 text-white px-6 py-3 rounded-lg font-medium transition shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                      </svg>
                      {t('home.purchaseBanner.button')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Passeios Section - Prateleira 1 */}
      <section id="passeios" className="py-20 bg-stone-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-light text-stone-800 mb-3">
              {t('home.ourTours')}
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto mb-6">
              {t('home.ourToursDesc')}
            </p>
          </div>

          {error ? (
            <div className="text-center py-12 text-red-600">{error}</div>
          ) : passeiosLang.length === 0 ? (
            <div className="text-center py-16 animate-on-scroll opacity-0">
              <div className="text-6xl mb-4">🌴</div>
              <p className="text-gray-500 text-xl mb-2">
                {t('home.comingSoon')}
              </p>
              <p className="text-gray-400">
                {t('home.comingSoonDesc')}
              </p>
            </div>
          ) : (
            <>
              {/* Prateleira 1 - Mais Populares com rolagem infinita */}
              <InfiniteCarousel 
                passeios={passeiosLang.slice(0, 5)} 
                title={t('home.mostPopular')} 
              />

              {/* Conteúdo entre prateleiras */}
              <div className="bg-stone-200/50 rounded-xl p-8 mb-16 text-center">
                <p className="text-stone-700 text-lg mb-4">
                  {t('home.exploreMore')}
                </p>
                <Link
                  to="/passeios"
                  className="inline-block px-6 py-3 bg-stone-800 text-white rounded-lg font-medium hover:bg-stone-700 transition"
                >
                  {t('home.viewAllTours')}
                </Link>
              </div>

              {/* Prateleira 2 - Destaques com rolagem infinita */}
              {passeiosLang.length > 5 && (
                <InfiniteCarousel 
                  passeios={passeiosLang.slice(5, 10)} 
                  title={t('home.highlights')} 
                />
              )}

              {/* Conteúdo final se houver mais passeios */}
              {passeiosLang.length > 10 && (
                <div className="bg-stone-800 text-white rounded-xl p-8 text-center">
                  <p className="text-xl mb-4">
                    {t('home.discoverMore')}
                  </p>
                  <Link
                    to="/passeios"
                    className="inline-block px-6 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition"
                  >
                    {t('home.viewAllTours')} ({passeios.length})
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Sobre Nós Section */}
      <section id="sobre" className="py-20 bg-stone-200/30 relative overflow-hidden">

        {/* Imagem do tucano voando à direita com animação */}
        <div className="absolute top-4 right-2 sm:top-6 sm:right-4 md:top-10 md:right-10 z-10 pointer-events-none opacity-60 sm:opacity-70 md:opacity-80">
          <img 
            src="/tucano_voando.png" 
            alt="Tucano voando" 
            className="w-24 sm:w-32 md:w-40 lg:w-56 xl:w-72 h-auto animate-fly"
            style={{ filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.2))' }}
          />
        </div>

        {/* Imagem do tucano voando à esquerda com animação */}
        <div className="absolute bottom-4 left-2 sm:bottom-6 sm:left-4 md:bottom-20 md:left-10 z-10 pointer-events-none opacity-60 sm:opacity-70 md:opacity-80" style={{ transform: 'scaleX(-1)' }}>
          <img 
            src="/tucano_voando.png" 
            alt="Tucano voando" 
            className="w-24 sm:w-32 md:w-40 lg:w-56 xl:w-72 h-auto animate-fly"
            style={{ 
              filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.2))',
              animationDelay: '2s'
            }}
          />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-light text-stone-800 mb-2">
                {t('about.title')}
              </h2>
              <p className="text-stone-600">
                {t('about.subtitle')}
              </p>
            </div>

            {/* Missão */}
            <div className="bg-white rounded-xl shadow-sm border border-stone-300/50 p-6 md:p-8 mb-6">
              <h3 className="text-xl md:text-2xl font-medium text-stone-900 mb-4">
                {t('about.mission.title')}
              </h3>
              <div className="space-y-4 text-stone-700">
                <p>
                  {t('about.mission.text1')}
                </p>
                <p className="border-l-3 border-amber-300 pl-4 italic text-stone-600">
                  {t('about.mission.text2')}
                </p>
              </div>
            </div>

            {/* História */}
            <div className="bg-white rounded-xl shadow-sm border border-stone-300/50 p-6 md:p-8 mb-6">
              <h3 className="text-xl md:text-2xl font-medium text-stone-900 mb-4">
                {t('about.history.title')}
              </h3>
              <div className="space-y-4 text-stone-700">
                <p>
                  {t('about.history.text1')}
                </p>
                <p className="bg-stone-100/50 rounded p-4 border-l-3 border-amber-300 italic">
                  {t('about.history.text2')}
                </p>
                <p>
                  {t('about.history.text3')}
                </p>
              </div>
            </div>

            {/* Valores */}
            <div className="bg-white rounded-xl shadow-sm border border-stone-300/50 p-6 md:p-8 mb-6">
              <h3 className="text-xl md:text-2xl font-medium text-stone-900 mb-6">{t('about.values.title')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-stone-50 rounded-lg p-4 border border-stone-200">
                  <div className="text-2xl mb-2">🤝</div>
                  <h4 className="font-medium text-stone-900 mb-2">{t('about.values.cooperation.title')}</h4>
                  <p className="text-stone-600 text-sm">
                    {t('about.values.cooperation.desc')}
                  </p>
                </div>
                <div className="bg-stone-50 rounded-lg p-4 border border-stone-200">
                  <div className="text-2xl mb-2">🙏</div>
                  <h4 className="font-medium text-stone-900 mb-2">{t('about.values.respect.title')}</h4>
                  <p className="text-stone-600 text-sm">
                    {t('about.values.respect.desc')}
                  </p>
                </div>
                <div className="bg-stone-50 rounded-lg p-4 border border-stone-200">
                  <div className="text-2xl mb-2">✨</div>
                  <h4 className="font-medium text-stone-900 mb-2">{t('about.values.quality.title')}</h4>
                  <p className="text-stone-600 text-sm">
                    {t('about.values.quality.desc')}
                  </p>
                </div>
                <div className="bg-stone-50 rounded-lg p-4 border border-stone-200">
                  <div className="text-2xl mb-2">🌟</div>
                  <h4 className="font-medium text-stone-900 mb-2">{t('about.values.transparency.title')}</h4>
                  <p className="text-stone-600 text-sm">
                    {t('about.values.transparency.desc')}
                  </p>
                </div>
              </div>
            </div>

            {/* Citação */}
            <div className="bg-stone-800 text-white rounded-xl p-8 md:p-10 text-center">
              <blockquote className="text-lg md:text-xl italic mb-4 max-w-2xl mx-auto">
                "{t('about.quote.text')}"
              </blockquote>
              <p className="text-stone-300 text-sm">
                {t('about.quote.author')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Por que escolher Section */}
      <section className="py-16 bg-stone-200/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-medium text-stone-800 mb-8 text-center">
              {t('about.whyChoose.title')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { title: t('about.whyChoose.professionals.title'), desc: t('about.whyChoose.professionals.desc') },
                { title: t('about.whyChoose.prices.title'), desc: t('about.whyChoose.prices.desc') },
                { title: t('about.whyChoose.local.title'), desc: t('about.whyChoose.local.desc') }
              ].map((item, index) => (
                <div key={index} className="text-center p-5 bg-white rounded-lg shadow-sm border border-stone-200">
                  <h3 className="font-medium text-stone-900 mb-2">{item.title}</h3>
                  <p className="text-stone-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contato Section */}
      <section id="contato" className="py-16 bg-stone-800 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-medium mb-6">
              {t('home.contactTitle')}
            </h2>
            <p className="text-stone-300 mb-6">
              {t('home.contactDesc')}
            </p>
            <p className="text-stone-400 text-sm">
              {t('home.contactSubDesc')}
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-900 text-white py-10">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <img
                src="/logo-coop-igu.png"
                alt="COOP-IGU - Turismo • Receptivo • Eventos"
                className="h-10 md:h-12 w-auto mb-2 object-contain"
              />
              <p className="text-stone-400 text-sm mb-1">
                {t('home.footer.tagline')}
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-3">{t('home.footer.links')}</h4>
              <ul className="space-y-1 text-stone-400 text-sm">
                <li>
                  <button
                    onClick={() => {
                      const element = document.getElementById('inicio');
                      if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="hover:text-white transition"
                  >
                    {t('header.home')}
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {
                      const element = document.getElementById('passeios');
                      if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="hover:text-white transition"
                  >
                    {t('header.tours')}
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => {
                      const element = document.getElementById('sobre');
                      if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="hover:text-white transition"
                  >
                    {t('header.about')}
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-3">{t('home.footer.socialMedia')}</h4>
              <a
                href="https://www.instagram.com/coop_igu/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-stone-400 hover:text-pink-400 transition text-sm mb-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                @coop_igu
              </a>
              <div className="mt-4">
                <h4 className="font-medium mb-3">{t('home.footer.location')}</h4>
                <p className="text-stone-400 text-sm">
                  {t('home.footer.address')}
                </p>
                <p className="text-stone-400 text-sm">
                  {t('home.footer.city')}
                </p>
              </div>
            </div>
          </div>
          <div className="border-t border-stone-800 pt-6 text-center text-stone-500 text-sm">
            <p>&copy; {new Date().getFullYear()} COOP-IGU. {t('home.footer.copyright')}</p>
          </div>
        </div>
      </footer>
      </div>
    </div>
  );
}

export default Home;
